/**
 * The competition hubs of wave 3 (non-football): the readers of a partner's event name, the hand-entered rivalry
 * and trophy lists, and the pickers that say which catalog rows belong to which hub. Pure: used by the registry
 * (shared/tournaments.mjs), the builders (scripts/lib/comp-builders.mjs) and their tests; nothing here reads a
 * file or the network.
 *
 *   kind 'us-season'    a US league's season: NBA, NHL, NFL, college basketball (rivalries, home games by club, weeks)
 *   kind 'circuit'      race weekends: Formula 1, NASCAR (a weekend = one track, three or four days)
 *   kind 'slam'         tennis sessions by day: US Open, National Bank Open, WTA Finals (day and night)
 *   kind 'fight-card'   UFC and boxing cards with a headline fight, a venue and a date
 *   kind 'round-robin'  Six Nations: five rounds, every nation home and away
 *
 * WHAT A HUB MAY SHOW follows shared/legal.mjs exactly (the builders drop a row `notPromoted` says is held back:
 * the Australian Open, Italian and French tennis, Irish designated venues, non-debenture Wimbledon). A hub is
 * a promotion surface, so a row that is listed but "never promoted" does not appear on it either.
 */

/** Lower case, no accents: "São Paulo" = "sao paulo". @param {unknown} s */
export const fold = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

// ---- pickers: which rows a hub reads (the row = { name, kind, category, venue, ... } from scripts/gen-longtail.mjs) ------------

/** @typedef {{ name?: string | null, kind?: string | null, category?: string | null, venue?: string | null, tournament?: string | null }} PickRow */
const OFF_TRACK = /after ?party|afterparty|podcast|hospitality|paddock club|fan ?zone|drift|\bparking\b|in schools|exhibition game|watch party/i;

/** @param {string} kind */
export const leaguePicker = (kind) => /** @param {PickRow} r */ (r) => r.category === 'Sports' && r.kind === kind && !OFF_TRACK.test(String(r.name ?? ''));
/** Formula 1 race weekends: a grand prix by name, never an afterparty or Formula Drift. @param {PickRow} r */
export const pickF1 = (r) => /\bformula (?:1|one)\b/i.test(String(r.name ?? '')) && /\bgrand prix\b|\bgp\b/i.test(String(r.name ?? '')) && !OFF_TRACK.test(String(r.name ?? ''));
/** @param {PickRow} r */
export const pickNascar = (r) => /\bnascar\b/i.test(String(r.name ?? '')) && !OFF_TRACK.test(String(r.name ?? '')) && !/hall of fame|garage|experience|tour\b/i.test(String(r.name ?? ''));
/** @param {RegExp} re @returns {(r: PickRow) => boolean} */
export const pickTennis = (re) => (r) => (r.category === 'Sports' || r.category === 'Theater') && re.test(`${r.name ?? ''} ${r.tournament ?? ''}`) && !/\bvs?\.?\s|a\.?f\.?c\.?(?![a-z])|\bfc\b/i.test(String(r.name ?? ''));
/** UFC and boxing/MMA cards; Fury vs Joshua has a page of its own (another builder), LA28 has its sport pages. @param {PickRow} r */
export const pickFights = (r) =>
  ['boxing', 'mma', 'mixed_martial_arts'].includes(String(r.kind ?? '')) &&
  !/fury\b.*\bjoshua|joshua\b.*\bfury/i.test(String(r.name ?? '')) &&
  !/summer games|los angeles 2028|golden gloves|student fight/i.test(String(r.name ?? ''));
/** @param {PickRow} r */
export const pickSixNations = (r) => /^six nations\b/i.test(String(r.name ?? '')) && /\bvs?\.?\s/i.test(String(r.name ?? '')) && !OFF_TRACK.test(String(r.name ?? ''));

// ---- Formula 1 / NASCAR ---------------------------------------------------------------------------------------------

const DAY_WORDS = { thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

/**
 * One Formula 1 listing from its partner name:
 *   TicketNetwork "2027 Formula 1: Monaco Grand Prix - Saturday" / "2026 Formula 1: Mexican City Grand Prix - 3 Day Pass"
 *   HelloTickets  "Formula 1 US GP 2026 - 3-Day Pass (Fri-Sun)" / "Formula 1 Australian Grand Prix 2027 - Thursday"
 * @param {string} name
 * @returns {{ race: string, year: number | null, day: 'Thu' | 'Fri' | 'Sat' | 'Sun' | null, pass: number | null } | null}
 */
export const parseF1 = (name) => {
  const s = String(name ?? '').replace(/\s+/g, ' ').trim();
  if (!/\bformula (?:1|one)\b/i.test(s) || !/\bgrand prix\b|\bgp\b/i.test(s)) return null;
  const [headRaw, ...rest] = s.split(/\s+[-–—]\s+/);
  const tail = rest.join(' - ');
  const year = Number(headRaw.match(/\b(20\d\d)\b/)?.[1] ?? s.match(/\b(20\d\d)\b/)?.[1]) || null;
  let race = headRaw
    .replace(/\bformula (?:1|one)\b:?/i, '')
    .replace(/\b20\d\d\b/g, '')
    .replace(/\bGP\b/g, 'Grand Prix')
    .replace(/\s+/g, ' ')
    .replace(/^[\s:–-]+|[\s:–-]+$/g, '');
  race = race.replace(/^Mexican City\b/i, 'Mexico City').replace(/^Sao Paulo\b/i, 'São Paulo').replace(/^Mexico City Grand Prix$/i, 'Mexico City Grand Prix');
  if (!race || !/grand prix/i.test(race)) return null;
  const passN = tail.match(/\b(\d)[- ]?day\b/i)?.[1];
  const dayHit = tail.match(/\b(thursday|friday|saturday|sunday)\b/i)?.[1]?.toLowerCase();
  return { race, year, day: passN ? null : dayHit ? /** @type {any} */ (DAY_WORDS[dayHit]) : null, pass: passN ? Number(passN) : null };
};

/**
 * The country of a grand prix by the race's own name (folded, without "grand prix"), hand-entered: a partner's `country` field is
 * sometimes wrong for a street circuit (Baku came as "US"). Races not here keep the partner's country.
 * @type {Record<string, string>}
 */
export const F1_COUNTRY = {
  bahrain: 'BH', 'saudi arabian': 'SA', australian: 'AU', japanese: 'JP', chinese: 'CN', miami: 'US', 'emilia romagna': 'IT', monaco: 'MC',
  canadian: 'CA', spanish: 'ES', austrian: 'AT', british: 'GB', belgian: 'BE', hungarian: 'HU', dutch: 'NL', italian: 'IT', azerbaijan: 'AZ',
  singapore: 'SG', us: 'US', 'united states': 'US', 'mexico city': 'MX', 'sao paulo': 'BR', 'las vegas': 'US', qatar: 'QA', 'abu dhabi': 'AE',
  portugal: 'PT', turkish: 'TR', turkiye: 'TR',
};

/** NASCAR's three national series, from the name. @param {string} name @returns {'Cup' | 'O\'Reilly' | 'Truck' | 'ARCA' | null} */
export const nascarSeries = (name) => {
  const s = String(name ?? '');
  if (/cup series/i.test(s)) return 'Cup';
  if (/o.?reilly/i.test(s)) return "O'Reilly";
  if (/truck series/i.test(s)) return 'Truck';
  if (/\barca\b/i.test(s)) return 'ARCA';
  return null;
};

/** The race's own name where the partner gives one ("Cup Series: Daytona 500" → "Daytona 500"), else null. @param {string} name */
export const nascarRaceName = (name) => {
  const s = String(name ?? '').replace(/\s+/g, ' ');
  const after = s.match(/series(?: & ARCA Menards Series)?\s*:\s*(.+)$/i)?.[1]?.trim();
  if (after && !/^championship race$/i.test(after)) return after;
  const before = s.match(/^(.+?)\s+NASCAR\b/i)?.[1]?.trim();
  return before ?? null;
};

// ---- tennis sessions ------------------------------------------------------------------------------------------------

const COURT_RE = /\b(Arthur Ashe|Louis Armstrong|Stadium \d|Center Court|Centre Court|No\.? ?1 Court|Rod Laver Arena|John Cain Arena|Margaret Court Arena|Philippe Chatrier|Suzanne Lenglen|Grandstand|Estadio Manolo Santana)\b/i;
const ROUND_PATTERNS = [
  [/\bqualif/i, 'Qualifying'],
  [/\b(?:first|1st) round\b|\bround 1\b/i, 'Round 1'],
  [/\b(?:second|2nd) round\b|\bround 2\b/i, 'Round 2'],
  [/\b(?:third|3rd) round\b|\bround 3\b/i, 'Round 3'],
  [/\bround of 16\b|\b(?:fourth|4th) round\b/i, 'Round of 16'],
  [/\bqua?r?ter[- ]?fina/i, 'Quarter-finals'],
  [/\bsemi[- ]?fina/i, 'Semi-finals'],
  [/\bfinals?\b/i, 'Final'],
];

/**
 * One tennis listing from its partner name: a numbered session, a grounds pass, day or night, the round when the
 * name says it. The round is NEVER guessed from the date: "Session 1" stays "Session 1".
 *   "2027 US Open Tennis Championships - Session 2"            -> session 2
 *   "2027 US Open Tennis Championships: Grounds Pass - Monday Eve Admission" -> grounds, night
 *   "National Bank Open: Session 9 - Second Round"            -> session 9, Round 2
 * @param {string} name @param {string | null | undefined} [time] "HH:MM" @param {string | null | undefined} [venue] the partner's venue text (a court named there counts)
 * @returns {{ no: number | null, grounds: boolean, part: 'day' | 'night' | null, round: string | null, court: string | null }}
 */
export const parseTennisSession = (name, time = null, venue = null) => {
  const s = String(name ?? '');
  const grounds = /\bgrounds?\b.*\b(?:pass|admission)\b/i.test(s);
  const no = Number(s.match(/\bsession\s+(\d+)\b/i)?.[1]) || null;
  // the tournament's own name is no round: "Nitto ATP Finals" / "WTA Finals" / "Next Gen ATP Finals" put "Final" on every session
  const roundText = s.replace(/^.*?\bsession\s+\d+\b/i, '').replace(/\b(?:atp|wta)(?:\s+\w+)?\s+finals?\b|\bnext\s*gen\s+finals?\b/gi, ' ');
  const round = ROUND_PATTERNS.find(([re]) => re.test(roundText))?.[1] ?? null;
  let part = /\b(?:eve|evening|night)\b/i.test(s) ? 'night' : /\b(?:morning|afternoon|day session)\b/i.test(s) ? 'day' : null;
  if (!part && time && /^\d{1,2}:\d{2}/.test(time)) part = Number(time.slice(0, 2)) >= 17 ? 'night' : 'day';
  const court = (s.match(COURT_RE) ?? String(venue ?? '').match(COURT_RE))?.[1]?.replace(/^no\.? ?1 court$/i, 'No. 1 Court') ?? null;
  return { no, grounds, part: /** @type {any} */ (part), round, court };
};

// ---- fights ---------------------------------------------------------------------------------------------------------

/** Promotions, by name; `major` ones lead the page. */
const PROMOTIONS = [
  [/^\s*ufc\b/i, 'UFC', true],
  [/\bbkfc\b|bare knuckle/i, 'BKFC', true],
  [/top rank/i, 'Top Rank', true],
  [/matchroom/i, 'Matchroom', true],
  [/golden boy/i, 'Golden Boy', true],
  [/\bpbc\b|premier boxing champions/i, 'PBC', true],
  [/\bdazn\b/i, 'DAZN', true],
  [/zuffa/i, 'Zuffa Boxing', true],
  [/most valuable promotions|\bmvp\b/i, 'MVP', true],
  [/\blfa\b|legacy fighting/i, 'LFA', false],
  [/cage fury/i, 'Cage Fury', false],
  [/island fights/i, 'Island Fights', false],
  [/ring of combat/i, 'Ring of Combat', false],
  [/combat zone/i, 'Combat Zone', false],
  [/fierce fighting/i, 'Fierce FC', false],
  [/king of the cage/i, 'King of the Cage', false],
  [/mike tyson/i, 'Tyson Invitational', false],
];

const ROMAN = { I: 1, V: 5, X: 10, L: 50, C: 100 };
/** @param {string} s */
const romanToInt = (s) => [...s].reduce((n, ch, i, a) => (ROMAN[/** @type {'I'} */ (ch)] < (ROMAN[/** @type {'I'} */ (a[i + 1])] ?? 0) ? n - ROMAN[/** @type {'I'} */ (ch)] : n + ROMAN[/** @type {'I'} */ (ch)]), 0);

/**
 * A fight card from its name: the promotion, its number ("UFC 334" -> 334), the main event ("Gane vs Hokit").
 * @param {string} name
 * @returns {{ promotion: string | null, major: boolean, number: number | null, main: string | null }}
 */
export const parseFight = (name) => {
  const s = String(name ?? '').replace(/\s+/g, ' ').trim();
  const p = PROMOTIONS.find(([re]) => re.test(s));
  const head = s.split(/\s*[:–—]\s*|\s+-\s+/)[0];
  const num = head.match(/\b(\d{1,3})\b/)?.[1];
  const rom = head.match(/\b([IVXL]{2,6})\b/)?.[1];
  const number = num ? Number(num) : rom ? romanToInt(rom) : null;
  // the part after the last ":" / " - " when it has a "vs", else the whole name
  const parts = s.split(/\s*[:–—]\s*|\s+-\s+/);
  const vs = [...parts].reverse().find((x) => /\bvs?\.?\s/i.test(x)) ?? null;
  const main = vs ? vs.replace(/\s+vs?\.?\s+/i, ' vs. ').trim() : null;
  return { promotion: p?.[1] ?? null, major: !!p?.[2], number: number !== null && number < 600 ? number : null, main };
};

// ---- Six Nations ----------------------------------------------------------------------------------------------------

/** "Six Nations Tournament: Scotland vs. Italy" -> { home: 'Scotland', away: 'Italy' } (the first nation is at home). @param {string} name */
export const parseSixNations = (name) => {
  const m = String(name ?? '').match(/^six nations(?: tournament)?\s*:\s*(.+?)\s+vs?\.?\s+(.+)$/i);
  return m ? { home: m[1].trim(), away: m[2].trim() } : null;
};

/**
 * The named trophy of a Six Nations pairing (either way round): hand-entered, each a long-standing prize between two nations
 * (Wikipedia: Six Nations Championship, "Other trophies"). A pairing not here has no named trophy.
 * @type {{ pair: [string, string], name: string }[]}
 */
export const SIX_NATIONS_TROPHIES = [
  { pair: ['England', 'Scotland'], name: 'Calcutta Cup' },
  { pair: ['England', 'Ireland'], name: 'Millennium Trophy' },
  { pair: ['Ireland', 'Scotland'], name: 'Centenary Quaich' },
  { pair: ['France', 'Scotland'], name: 'Auld Alliance Trophy' },
  { pair: ['France', 'Italy'], name: 'Giuseppe Garibaldi Trophy' },
  { pair: ['Scotland', 'Wales'], name: 'Doddie Weir Cup' },
];
/** @param {string} a @param {string} b */
export const trophyOf = (a, b) => SIX_NATIONS_TROPHIES.find((t) => (t.pair[0] === a && t.pair[1] === b) || (t.pair[0] === b && t.pair[1] === a))?.name ?? null;

// ---- US leagues: rivalries, weeks, marquee dates ------------------------------------------------------------------------

/**
 * Well-known rivalries, hand-entered by the clubs' short names as SeatGeek writes them (the partner's `teams`). A game is a
 * "Rivalry" when both of its clubs are in one pair; which of them matter most is read from OUR demand score, not from here.
 * @type {Record<string, [string, string][]>}
 */
export const RIVALRIES = {
  nba: [
    ['Lakers', 'Celtics'], ['Lakers', 'Clippers'], ['Lakers', 'Warriors'], ['Celtics', 'Knicks'], ['Celtics', '76ers'], ['Celtics', 'Heat'],
    ['Knicks', 'Nets'], ['Knicks', 'Heat'], ['Bulls', 'Pistons'], ['Mavericks', 'Spurs'], ['Rockets', 'Spurs'], ['Mavericks', 'Rockets'],
    ['Heat', 'Magic'], ['Warriors', 'Clippers'], ['Suns', 'Lakers'], ['Bulls', 'Knicks'],
  ],
  nhl: [
    ['Bruins', 'Canadiens'], ['Maple Leafs', 'Canadiens'], ['Bruins', 'Maple Leafs'], ['Rangers', 'Islanders'], ['Rangers', 'Devils'], ['Rangers', 'Flyers'],
    ['Flyers', 'Penguins'], ['Penguins', 'Capitals'], ['Blackhawks', 'Blues'], ['Red Wings', 'Blackhawks'], ['Flames', 'Oilers'], ['Kings', 'Ducks'],
    ['Kings', 'Sharks'], ['Lightning', 'Panthers'], ['Avalanche', 'Golden Knights'], ['Canucks', 'Flames'],
  ],
  nfl: [
    ['Cowboys', 'Eagles'], ['Cowboys', 'Commanders'], ['Cowboys', 'Giants'], ['Giants', 'Eagles'], ['Packers', 'Bears'], ['Packers', 'Vikings'],
    ['Ravens', 'Steelers'], ['Browns', 'Steelers'], ['Chiefs', 'Raiders'], ['Chiefs', 'Broncos'], ['49ers', 'Seahawks'], ['49ers', 'Rams'],
    ['Patriots', 'Jets'], ['Patriots', 'Bills'], ['Bills', 'Dolphins'], ['Bears', 'Lions'], ['Chargers', 'Raiders'], ['Rams', 'Seahawks'],
    ['Saints', 'Falcons'], ['Colts', 'Titans'], ['Steelers', 'Bengals'],
  ],
  ncaa_basketball: [
    ['Duke', 'North Carolina'], ['Kentucky', 'Louisville'], ['Kansas', 'Missouri'], ['Indiana', 'Purdue'], ['Michigan', 'Ohio State'],
    ['Michigan', 'Michigan State'], ['Syracuse', 'Georgetown'], ['Arizona', 'Arizona State'], ['Alabama', 'Auburn'], ['Kansas', 'Kansas State'],
    ['Duke', 'Virginia'], ['Villanova', 'Georgetown'], ['UCLA', 'USC'], ['Gonzaga', 'Saint Mary\'s'], ['Kentucky', 'Tennessee'], ['Kansas', 'Kentucky'],
  ],
};

/** The pair of a game if it is a known rivalry. @param {string} league @param {string[]} teams */
export const rivalryOf = (league, teams) => {
  const a = fold(teams[0]);
  const b = fold(teams[1]);
  return (RIVALRIES[league] ?? []).some(([x, y]) => (fold(x) === a && fold(y) === b) || (fold(x) === b && fold(y) === a));
};

/** Monday of the week containing `iso` (weeks run Mon-Sun), or Thursday for the NFL's Thu-Wed week. @param {string} iso @param {'mon' | 'thu'} [start] */
export const weekStartOf = (iso, start = 'mon') => {
  const d = new Date(`${iso}T12:00:00Z`);
  const dow = d.getUTCDay(); // 0 Sun
  const back = start === 'thu' ? (dow + 3) % 7 : (dow + 6) % 7;
  return new Date(d.getTime() - back * 864e5).toISOString().slice(0, 10);
};

/** The nth (1-based) given weekday (0 Sun .. 6 Sat) of a month, ISO date. @param {number} year @param {number} month 1-12 @param {number} dow @param {number} nth */
export const nthWeekday = (year, month, dow, nth) => {
  const first = new Date(Date.UTC(year, month - 1, 1, 12));
  const shift = (dow - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, month - 1, 1 + shift + (nth - 1) * 7, 12)).toISOString().slice(0, 10);
};

/**
 * The calendar days a US season is known for, as the data can show them. `label` is a plain name; a day with no listed game
 * does not appear (the builder checks).
 * @param {number} fromYear the year the season starts in (2026 for 2026-27)
 * @param {string} league
 * @returns {{ key: string, label: string, date: string }[]}
 */
export const marqueeDays = (fromYear, league) => {
  const y2 = fromYear + 1;
  const days = [
    { key: 'thanksgiving', label: 'Thanksgiving', date: nthWeekday(fromYear, 11, 4, 4) },
    { key: 'black-friday', label: 'Day after Thanksgiving', date: nthWeekday(fromYear, 11, 5, 4) },
    { key: 'christmas-eve', label: 'Christmas Eve', date: `${fromYear}-12-24` },
    { key: 'christmas', label: 'Christmas Day', date: `${fromYear}-12-25` },
    { key: 'nye', label: "New Year's Eve", date: `${fromYear}-12-31` },
    { key: 'nyd', label: "New Year's Day", date: `${y2}-01-01` },
  ];
  if (league === 'nba' || league === 'ncaa_basketball') days.push({ key: 'mlk', label: 'Martin Luther King Jr. Day', date: nthWeekday(y2, 1, 1, 3) });
  if (league === 'nfl') days.push({ key: 'sb', label: 'Super Bowl Sunday', date: nthWeekday(y2, 2, 0, 2) });
  return days.sort((a, b) => a.date.localeCompare(b.date));
};
